import { useState, useEffect, useCallback } from "react";
import TradePerformance from "./TradePerformance";
import { API_BASE } from "../App";
import { darkTheme, lightTheme } from "../theme";

let t = darkTheme;

// ─── Fonts ────────────────────────────────────────────────────────────────────
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  `}</style>
);

// ─── API Hook ─────────────────────────────────────────────────────────────────
function useAPI(endpoint, token, skip = false) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(() => {
    if (!endpoint || !token || skip) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API_BASE}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [endpoint, token, skip]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, refetch };
}

// ─── Shared Components ────────────────────────────────────────────────────────
function Card({ children, th, style = {} }) {
  return (
    <div style={{ background: th.surface, border: `1px solid ${th.border}`, ...style }}>
      {children}
    </div>
  );
}

function SectionHeader({ title, th, accent }) {
  return (
    <div style={{
      padding: "14px 20px",
      borderBottom: `1px solid ${th.border}`,
      display: "flex", alignItems: "center", gap: 10,
      background: `linear-gradient(90deg, rgba(201,168,76,0.04) 0%, transparent 100%)`,
    }}>
      <div style={{ width: 3, height: 14, background: accent || t.gold, borderRadius: 2 }} />
      <span style={{
        fontFamily: t.fontBody, fontSize: 11, fontWeight: t.weightSub,
        color: th.textMuted, letterSpacing: 3, textTransform: "uppercase",
      }}>{title}</span>
    </div>
  );
}

function StatCard({ label, value, sub, th, accent, mono }) {
  return (
    <div style={{
      background: th.surface,
      border: `1px solid ${th.border}`,
      borderTop: `2px solid ${accent || t.gold}`,
      padding: "18px 22px",
      flex: 1, minWidth: 140,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: t.goldGradient,
      }} />
      <p style={{ fontFamily: t.fontBody, fontSize: 10, fontWeight: t.weightSub, color: th.textMuted, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 8px" }}>{label}</p>
      <p style={{ fontFamily: mono ? t.fontMono : t.fontDisplay, fontWeight: t.weightDisplay, fontSize: mono ? 18 : 24, color: th.text, margin: "0 0 4px" }}>{value}</p>
      {sub && <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 11, margin: 0 }}>{sub}</p>}
    </div>
  );
}

function Loader({ th }) {
  return <p style={{ fontFamily: t.fontBody, color: th.textMuted, textAlign: "center", padding: 48, fontSize: 12, letterSpacing: 2 }}>Loading...</p>;
}

function AlertBadge({ count }) {
  if (!count) return null;
  return (
    <span style={{
      background: t.primary, color: "#fff",
      width: 18, height: 18, borderRadius: "50%",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: 10, fontFamily: t.fontBody, fontWeight: t.weightSub, marginLeft: 6,
    }}>{count}</span>
  );
}

// ─── Tax Side ─────────────────────────────────────────────────────────────────
function TaxSide({ user, token, th }) {
  const { data: profile } = useAPI("/api/tax/profile", token, !user.services?.includes("tax"));
  const { data: docs, refetch: refetchDocs } = useAPI(`/api/tax/documents/${user.username}`, token, !user.services?.includes("tax"));
  const { data: messages, refetch: refetchMsgs } = useAPI(`/api/messages/${user.username}`, token);

  const [activeTab, setActiveTab] = useState("overview");
  const [msgBody,   setMsgBody]   = useState("");
  const [msgSubj,   setMsgSubj]   = useState("");
  const [sending,   setSending]   = useState(false);

  const unreadCount = messages?.filter(m => !m.read && m.to === user.username)?.length || 0;

  const sendMessage = async () => {
    setSending(true);
    await fetch(`${API_BASE}/api/messages/send`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ to: "admin", subject: msgSubj, body: msgBody }),
    });
    setMsgBody(""); setMsgSubj("");
    setSending(false);
    refetchMsgs();
  };

  const TABS = [
    { key: "overview",  label: "Overview"  },
    { key: "documents", label: "Documents" },
    { key: "messages",  label: "Messages", badge: unreadCount },
  ];

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: t.fontDisplay, fontWeight: t.weightDisplay, fontSize: 32, color: th.text, letterSpacing: 1, margin: "0 0 4px" }}>
          Wayne Tax & Advisory
        </h1>
        <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 12, letterSpacing: 3, textTransform: "uppercase", margin: 0 }}>
          Client Tax Portal
        </p>
      </div>

      <div style={{ display: "flex", gap: 2, marginBottom: 28, borderBottom: `1px solid ${th.border}` }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            background: "transparent",
            color: activeTab === tab.key ? t.gold : th.textMuted,
            border: "none",
            borderBottom: `2px solid ${activeTab === tab.key ? t.gold : "transparent"}`,
            padding: "12px 20px", fontSize: 11, fontFamily: t.fontBody,
            fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase",
            cursor: "pointer", transition: "all 0.15s",
            display: "flex", alignItems: "center",
          }}>
            {tab.label}
            {tab.badge ? <AlertBadge count={tab.badge} /> : null}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <StatCard label="Federal Bracket" value={`${((profile?.tax_bracket||0)*100).toFixed(0)}%`} th={th} mono />
            <StatCard label="State Tax"        value={`${((profile?.state_tax||0)*100).toFixed(1)}%`}  th={th} mono />
            <StatCard label="LTCG Rate"        value={`${((profile?.ltcg_rate||0)*100).toFixed(0)}%`}  th={th} mono />
            <StatCard label="Effective Rate"   value={`${((profile?.effective_combined||0)*100).toFixed(1)}%`} th={th} mono accent={t.primary} />
          </div>
          <Card th={th} style={{ padding: 28 }}>
            <p style={{ fontFamily: t.fontDisplay, fontWeight: t.weightDisplay, fontSize: 20, color: th.text, margin: "0 0 20px" }}>
              Your Tax Summary
            </p>
            <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Your personalized tax profile is maintained by Wayne Tax & Advisory. Your advisor reviews
              your tax situation throughout the year to identify planning opportunities. Contact us via
              the Messages tab with any questions.
            </p>
          </Card>
        </div>
      )}

      {activeTab === "documents" && (
        <Card th={th}>
          <SectionHeader title="Your Documents" th={th} />
          {!docs?.length ? (
            <p style={{ fontFamily: t.fontBody, color: th.textMuted, textAlign: "center", padding: 48, fontSize: 12 }}>
              No documents uploaded yet
            </p>
          ) : (
            docs.map(doc => (
              <div key={doc.filename} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "14px 20px", borderBottom: `1px solid ${th.border}`,
              }}>
               <div>
                  <p style={{ fontFamily: t.fontBody, color: th.text, fontSize: 13, margin: "0 0 2px" }}>{doc.filename}</p>
                  <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 11, margin: 0 }}>
                    {new Date(doc.uploaded_at).toLocaleDateString()} · {(doc.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={() => window.open(`${API_BASE}/api/tax/documents/${user.username}/download/${doc.filename}`, '_blank')}
                  style={{
                    background: "transparent", color: t.gold,
                    border: `1px solid ${t.borderGold}`,
                    padding: "6px 14px", fontSize: 10, fontFamily: t.fontBody,
                    letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
                  }}
                >Download</button>
              </div>
            ))
          )}
        </Card>
      )}

      {activeTab === "messages" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card th={th}>
            <SectionHeader title="Inbox" th={th} />
            {!messages?.length ? (
              <p style={{ fontFamily: t.fontBody, color: th.textMuted, textAlign: "center", padding: 48, fontSize: 12 }}>No messages</p>
            ) : (
              messages.filter(m => m.to === user.username).map((msg, i) => (
                <div key={i} style={{
                  padding: "14px 20px", borderBottom: `1px solid ${th.border}`,
                  background: !msg.read ? `rgba(201,168,76,0.06)` : "transparent",
                  borderLeft: `3px solid ${!msg.read ? t.gold : "transparent"}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontFamily: t.fontBody, color: th.text, fontSize: 12, fontWeight: t.weightSub }}>{msg.subject}</span>
                    {!msg.read && <span style={{ background: t.gold, color: "#0A0A0A", fontSize: 9, padding: "2px 6px", fontFamily: t.fontBody, letterSpacing: 1 }}>NEW</span>}
                  </div>
                  <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 11, margin: "0 0 4px" }}>{msg.body}</p>
                  <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 10, margin: 0, opacity: 0.6 }}>
                    {new Date(msg.timestamp).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </Card>

          <Card th={th} style={{ padding: 24 }}>
            <p style={{ fontFamily: t.fontDisplay, fontWeight: t.weightDisplay, fontSize: 18, color: th.text, margin: "0 0 20px" }}>Message Your Advisor</p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontFamily: t.fontBody, fontSize: 10, color: th.textMuted, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>Subject</label>
              <input
                value={msgSubj}
                onChange={e => setMsgSubj(e.target.value)}
                style={{
                  width: "100%", background: th.bg || th.bgDeep, border: "none",
                  borderBottom: `1px solid ${t.borderGold}`, color: th.text,
                  padding: "10px 0", fontSize: 13, fontFamily: t.fontBody,
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontFamily: t.fontBody, fontSize: 10, color: th.textMuted, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>Message</label>
              <textarea
                value={msgBody}
                onChange={e => setMsgBody(e.target.value)}
                rows={5}
                style={{
                  width: "100%", background: th.bg || th.bgDeep, border: "none",
                  borderBottom: `1px solid ${t.borderGold}`, color: th.text,
                  padding: "10px", fontSize: 13, fontFamily: t.fontBody,
                  outline: "none", resize: "vertical", boxSizing: "border-box",
                }}
              />
            </div>
            <button onClick={sendMessage} disabled={sending || !msgBody} style={{
              background: t.goldGradient, color: "#0A0A0A",
              border: "none", padding: "12px 24px", fontSize: 11, fontFamily: t.fontBody,
              fontWeight: t.weightDisplay, letterSpacing: 3, textTransform: "uppercase",
              cursor: sending || !msgBody ? "not-allowed" : "pointer",
              opacity: !msgBody ? 0.5 : 1,
            }}>
              {sending ? "Sending..." : "Send Message"}
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Investing Side ───────────────────────────────────────────────────────────
function InvestingSide({ user, token, th }) {
  const { data: positions } = useAPI("/api/investing/positions", token, !user.services?.includes("investing"));
  const { data: perf }      = useAPI("/api/investing/performance", token, !user.services?.includes("investing"));

  const [activeTab, setActiveTab] = useState("overview");

  const longs    = positions?.filter(p => p.side === "LONG")  || [];
  const shorts   = positions?.filter(p => p.side === "SHORT") || [];
  const totalVal = longs.reduce((s, p) => s + (p.dollar_amount || 0), 0);
  const avgConv  = longs.length ? longs.reduce((s, p) => s + (p.conviction_score || 0), 0) / longs.length : 0;

  const TABS = [
    { key: "overview",     label: "Overview"     },
    { key: "holdings",     label: "Holdings"     },
    { key: "performance",  label: "Performance"  },
    { key: "trades",       label: "Trade Analysis" },
  ];

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: t.fontDisplay, fontWeight: t.weightDisplay, fontSize: 32, color: th.text, letterSpacing: 1, margin: "0 0 4px" }}>
          Wayne Capital
        </h1>
        <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 12, letterSpacing: 3, textTransform: "uppercase", margin: 0 }}>
          Investment Portfolio
        </p>
      </div>

      <div style={{ display: "flex", gap: 2, marginBottom: 28, borderBottom: `1px solid ${th.border}` }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            background: "transparent",
            color: activeTab === tab.key ? t.gold : th.textMuted,
            border: "none",
            borderBottom: `2px solid ${activeTab === tab.key ? t.gold : "transparent"}`,
            padding: "12px 20px", fontSize: 11, fontFamily: t.fontBody,
            fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase",
            cursor: "pointer", transition: "all 0.15s",
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <StatCard label="Portfolio Value" value={`$${totalVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} th={th} mono />
            <StatCard label="Positions"       value={longs.length}  sub="Long positions" th={th} />
            <StatCard label="Short Positions" value={shorts.length} sub="Active shorts"  th={th} accent={t.primary} />
            <StatCard label="Avg Conviction"  value={`${(avgConv*100).toFixed(1)}%`}     th={th} mono />
            {perf && <StatCard label="Win Rate"  value={`${((perf.win_rate||0)*100).toFixed(1)}%`} sub={`${perf.total_trades} trades`} th={th} mono />}
            {perf && <StatCard label="Total P&L" value={`$${(perf.total_pnl||0).toLocaleString()}`} th={th} mono accent={perf.total_pnl >= 0 ? "#4A9B6F" : t.primary} />}
          </div>
          <Card th={th} style={{ padding: 28 }}>
            <p style={{ fontFamily: t.fontDisplay, fontWeight: t.weightDisplay, fontSize: 20, color: th.text, margin: "0 0 16px" }}>
              Portfolio Strategy
            </p>
            <p style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Wayne Capital employs a systematic, multi-signal investment approach combining quantitative
              analysis with disciplined risk management. Positions are sized based on conviction scores
              derived from 13+ independent signal layers, including machine learning models, options flow,
              insider activity, and macroeconomic factors.
            </p>
          </Card>
        </div>
      )}

      {activeTab === "holdings" && (
        <div>
          <Card th={th} style={{ marginBottom: 16 }}>
            <SectionHeader title={`Long Positions (${longs.length})`} th={th} />
            {longs.length === 0 ? (
              <p style={{ fontFamily: t.fontBody, color: th.textMuted, textAlign: "center", padding: 48, fontSize: 12 }}>No positions</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: th.surfaceHigh }}>
                      {["Ticker", "Signal", "Conviction", "Size", "Value", "Price", "Shares"].map(h => (
                        <th key={h} style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 10, fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase", textAlign: "left", padding: "10px 14px", borderBottom: `1px solid ${th.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {longs.map((p, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${th.border}` }}>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: t.gold, fontSize: 13 }}>{p.ticker}</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontBody, color: th.text, fontSize: 11 }}>{p.action}</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{((p.conviction_score||0)*100).toFixed(1)}%</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{((p.position_size_pct||0)*100).toFixed(2)}%</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>${(p.dollar_amount||0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{p.current_price ? `$${p.current_price}` : "—"}</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{p.shares}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {shorts.length > 0 && (
            <Card th={th}>
              <SectionHeader title={`Short Positions (${shorts.length})`} th={th} accent={t.primary} />
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: th.surfaceHigh }}>
                      {["Ticker", "Conviction", "Size", "Value", "Price", "Shares"].map(h => (
                        <th key={h} style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 10, fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase", textAlign: "left", padding: "10px 14px", borderBottom: `1px solid ${th.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shorts.map((p, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${th.border}` }}>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: t.primary, fontSize: 13 }}>{p.ticker}</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{((p.conviction_score||0)*100).toFixed(1)}%</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{((p.position_size_pct||0)*100).toFixed(2)}%</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>${(p.dollar_amount||0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{p.current_price ? `$${p.current_price}` : "—"}</td>
                        <td style={{ padding: "11px 14px", fontFamily: t.fontMono, color: th.text, fontSize: 12 }}>{p.shares}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === "performance" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <StatCard label="Total Trades" value={perf?.total_trades || 0}                              th={th} mono />
            <StatCard label="Win Rate"     value={`${((perf?.win_rate||0)*100).toFixed(1)}%`}           th={th} mono />
            <StatCard label="Total P&L"    value={`$${(perf?.total_pnl||0).toLocaleString()}`}          th={th} mono accent={perf?.total_pnl >= 0 ? "#4A9B6F" : t.primary} />
          </div>
        </div>
      )}

      {activeTab === "trades" && (
        <TradePerformance token={token} />
      )}
    </div>
  );
}

// ─── Main Client Portal ───────────────────────────────────────────────────────
export default function ClientPortal({ user, onLogout }) {
  const token    = user.access_token;
  const hasTax   = user.services?.includes("tax");
  const hasInv   = user.services?.includes("investing");
  const bothSvcs = hasTax && hasInv;

  const [side,      setSide]      = useState(hasTax ? "tax" : "investing");
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem(`theme_${user.username}`) || "dark"
  );
  t = themeMode === "dark" ? darkTheme : lightTheme;
  const th = themeMode === "dark" ? darkTheme : lightTheme;

  const toggleTheme = () => {
    const next = themeMode === "dark" ? "light" : "dark";
    setThemeMode(next);
    localStorage.setItem(`theme_${user.username}`, next);
  };

  return (
    <>
      <FontLink />
      <div style={{ minHeight: "100vh", background: th.bg, color: th.textPrimary, fontFamily: t.fontBody, transition: "background 0.3s" }}>

        {/* Top bar */}
        <div style={{
          background: th.navBg,
          borderBottom: `1px solid ${th.navBorder}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 32px", height: 64, position: "sticky", top: 0, zIndex: 100,
        }}>

          {/* Left — Wayne Enterprises logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
            <img src="/logo.png" alt="Wayne Enterprises" style={{ height: 40, width: "auto", flexShrink: 0 }} />
          </div>

          {/* Center — Capital / Tax toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, flex: 1, justifyContent: "center" }}>
            {/* Wayne Capital */}
            <button
              onClick={() => hasInv && setSide("investing")}
              style={{
                background: "transparent",
                border: "none",
                borderBottom: side === "investing" ? `2px solid ${t.gold}` : "2px solid transparent",
                padding: "20px 24px",
                cursor: hasInv ? "pointer" : "not-allowed",
                opacity: side === "investing" ? 1 : hasInv ? 0.5 : 0.25,
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{
                fontFamily: t.fontDisplay,
                fontWeight: t.weightDisplay,
                fontSize: 13,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: side === "investing" ? t.primary : t.primarySub,
              }}>
                Wayne Capital
              </span>
              {!hasInv && <span style={{ fontSize: 11, opacity: 0.6 }}>🔒</span>}
            </button>

            <div style={{ width: 1, height: 20, background: t.borderGold, opacity: 0.4, margin: "0 4px" }} />

            {/* Wayne Tax & Advisory */}
            <button
              onClick={() => hasTax && setSide("tax")}
              style={{
                background: "transparent",
                border: "none",
                borderBottom: side === "tax" ? `2px solid ${t.gold}` : "2px solid transparent",
                padding: "20px 24px",
                cursor: hasTax ? "pointer" : "not-allowed",
                opacity: side === "tax" ? 1 : hasTax ? 0.5 : 0.25,
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{
                fontFamily: t.fontDisplay,
                fontWeight: t.weightDisplay,
                fontSize: 13,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: side === "tax" ? t.primary : t.primarySub,
              }}>
                Wayne Tax & Advisory
              </span>
              {!hasTax && <span style={{ fontSize: 11, opacity: 0.6 }}>🔒</span>}
            </button>
          </div>

          {/* Right — theme toggle + user + logout */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, justifyContent: "flex-end" }}>
            <span style={{ fontFamily: t.fontBody, color: th.textMuted, fontSize: 12 }}>{user.name}</span>
            <button onClick={toggleTheme} style={{
              background: "transparent",
              border: `1px solid ${t.borderGold}`,
              padding: "6px 14px",
              fontSize: 10,
              fontFamily: t.fontBody,
              fontWeight: t.weightBody,
              letterSpacing: 2,
              textTransform: "uppercase",
              cursor: "pointer",
              color: t.gold,
            }}>
              {themeMode === "dark" ? "☀ Light" : "☾ Dark"}
            </button>
            <button onClick={onLogout} style={{
              background: "transparent", color: th.textMuted,
              border: `1px solid ${th.border}`, padding: "6px 14px",
              fontSize: 10, fontFamily: t.fontBody, letterSpacing: 2,
              textTransform: "uppercase", cursor: "pointer",
            }}>Logout</button>
          </div>
        </div>

        {/* Main content */}
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 40px" }}>
          {side === "tax"       && hasTax && <TaxSide       user={user} token={token} th={th} />}
          {side === "investing" && hasInv && <InvestingSide user={user} token={token} th={th} />}
          {side === "tax"       && !hasTax && (
            <p style={{ fontFamily: t.fontBody, color: th.textMuted, textAlign: "center", padding: 80 }}>
              You do not have access to tax services.
            </p>
          )}
          {side === "investing" && !hasInv && (
            <p style={{ fontFamily: t.fontBody, color: th.textMuted, textAlign: "center", padding: 80 }}>
              You do not have access to investment services.
            </p>
          )}
        </div>
      </div>
    </>
  );
}